import { motion, useInView } from "framer-motion";
import { useRef } from "react";

/**
 * AI Analysis — animated neural-flow infographic.
 * Inputs (your holdings) flow into an AI core that emits 3 insight pulses.
 */
const AiAnalysisPreview = () => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  const W = 540;
  const H = 380;

  // Left column: input nodes (holdings)
  const inputs = [
    { y: 70, label: "Holdings" },
    { y: 140, label: "Sectors" },
    { y: 210, label: "Dividends" },
    { y: 280, label: "Risk data" },
  ];

  // Right column: insight nodes
  const outputs = [
    { y: 100, label: "Concentration", tone: "hsl(var(--brand-amber))" },
    { y: 190, label: "Diversification", tone: "hsl(var(--primary))" },
    { y: 280, label: "Income outlook", tone: "hsl(var(--accent))" },
  ];

  const coreX = W / 2;
  const coreY = H / 2;
  const coreR = 38;

  return (
    <motion.div
      ref={ref}
      className="relative w-full max-w-[560px] mx-auto"
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="relative rounded-[2rem] bg-card border border-border/60 shadow-2xl p-6 overflow-hidden">
        <div
          className="absolute inset-0 opacity-70 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 50% 50%, hsl(var(--brand-amber) / 0.10), transparent 55%)",
          }}
        />

        <div className="relative flex items-center justify-between mb-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
              AI Reasoning
            </p>
            <p className="text-sm font-bold text-foreground mt-0.5">Live portfolio analysis</p>
          </div>
          <div className="flex items-center gap-1.5">
            <motion.span
              className="w-2 h-2 rounded-full bg-primary"
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 1.6, repeat: Infinity }}
            />
            <span className="text-[10px] font-bold uppercase tracking-wider text-primary">
              Thinking
            </span>
          </div>
        </div>

        <svg viewBox={`0 0 ${W} ${H}`} width="100%" className="block">
          <defs>
            <radialGradient id="ai-core" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.95" />
              <stop offset="60%" stopColor="hsl(var(--primary))" stopOpacity="0.65" />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
            </radialGradient>
            <linearGradient id="ai-line-in" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="hsl(var(--foreground))" stopOpacity="0.05" />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.7" />
            </linearGradient>
            <linearGradient id="ai-line-out" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.7" />
              <stop offset="100%" stopColor="hsl(var(--foreground))" stopOpacity="0.05" />
            </linearGradient>
          </defs>

          {/* Input → core lines */}
          {inputs.map((n, i) => {
            const x1 = 70;
            const y1 = n.y;
            const cx = (x1 + coreX) / 2;
            const path = `M ${x1} ${y1} Q ${cx} ${y1} ${coreX} ${coreY}`;
            return (
              <g key={`in-${i}`}>
                <motion.path
                  d={path}
                  fill="none"
                  stroke="url(#ai-line-in)"
                  strokeWidth={1.5}
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={inView ? { pathLength: 1, opacity: 1 } : {}}
                  transition={{ delay: 0.3 + i * 0.12, duration: 0.9, ease: "easeOut" }}
                />
                {/* moving particle */}
                <motion.circle
                  r={2.5}
                  fill="hsl(var(--primary))"
                  initial={{ opacity: 0 }}
                  animate={inView ? { opacity: [0, 1, 1, 0] } : {}}
                  transition={{
                    delay: 1.2 + i * 0.25,
                    duration: 1.8,
                    repeat: Infinity,
                    repeatDelay: 1.2,
                  }}
                >
                  <animateMotion
                    dur="1.8s"
                    repeatCount="indefinite"
                    begin={`${1.2 + i * 0.25}s`}
                    path={path}
                  />
                </motion.circle>
              </g>
            );
          })}

          {/* Core → output lines */}
          {outputs.map((n, i) => {
            const x2 = W - 170;
            const y2 = n.y;
            const cx = (coreX + x2) / 2;
            const path = `M ${coreX} ${coreY} Q ${cx} ${y2} ${x2} ${y2}`;
            return (
              <g key={`out-${i}`}>
                <motion.path
                  d={path}
                  fill="none"
                  stroke="url(#ai-line-out)"
                  strokeWidth={1.5}
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={inView ? { pathLength: 1, opacity: 1 } : {}}
                  transition={{ delay: 0.9 + i * 0.12, duration: 0.9, ease: "easeOut" }}
                />
                <motion.circle
                  r={2.5}
                  fill={n.tone}
                  initial={{ opacity: 0 }}
                  animate={inView ? { opacity: [0, 1, 1, 0] } : {}}
                  transition={{
                    delay: 1.6 + i * 0.3,
                    duration: 1.8,
                    repeat: Infinity,
                    repeatDelay: 1.0,
                  }}
                >
                  <animateMotion
                    dur="1.8s"
                    repeatCount="indefinite"
                    begin={`${1.6 + i * 0.3}s`}
                    path={path}
                  />
                </motion.circle>
              </g>
            );
          })}

          {/* Input nodes */}
          {inputs.map((n, i) => (
            <g key={`inn-${i}`}>
              <motion.circle
                cx={70}
                cy={n.y}
                r={6}
                fill="hsl(var(--card))"
                stroke="hsl(var(--border))"
                strokeWidth={1.5}
                initial={{ scale: 0 }}
                animate={inView ? { scale: 1 } : {}}
                transition={{ delay: 0.2 + i * 0.1, type: "spring", stiffness: 220 }}
                style={{ transformOrigin: `70px ${n.y}px` }}
              />
              <motion.text
                x={56}
                y={n.y + 3}
                textAnchor="end"
                className="fill-foreground/70"
                style={{ fontSize: 11, fontWeight: 600 }}
                initial={{ opacity: 0 }}
                animate={inView ? { opacity: 1 } : {}}
                transition={{ delay: 0.4 + i * 0.1 }}
              >
                {n.label}
              </motion.text>
            </g>
          ))}

          {/* Output nodes */}
          {outputs.map((n, i) => (
            <g key={`onn-${i}`}>
              <motion.circle
                cx={W - 170}
                cy={n.y}
                r={7}
                fill={n.tone}
                initial={{ scale: 0 }}
                animate={inView ? { scale: 1 } : {}}
                transition={{ delay: 0.9 + i * 0.12, type: "spring", stiffness: 220 }}
                style={{ transformOrigin: `${W - 170}px ${n.y}px` }}
              />
              <motion.circle
                cx={W - 170}
                cy={n.y}
                r={7}
                fill={n.tone}
                opacity={0.35}
                animate={{ scale: [1, 1.9, 1], opacity: [0.35, 0, 0.35] }}
                transition={{ duration: 2.2, repeat: Infinity, delay: 1.4 + i * 0.3 }}
                style={{ transformOrigin: `${W - 170}px ${n.y}px` }}
              />
              <motion.text
                x={W - 156}
                y={n.y + 3}
                className="fill-foreground"
                style={{ fontSize: 11, fontWeight: 700 }}
                initial={{ opacity: 0 }}
                animate={inView ? { opacity: 1 } : {}}
                transition={{ delay: 1.0 + i * 0.12 }}
              >
                {n.label}
              </motion.text>
            </g>
          ))}

          {/* Core */}
          <motion.circle
            cx={coreX}
            cy={coreY}
            r={coreR + 14}
            fill="url(#ai-core)"
            animate={{ scale: [1, 1.08, 1] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            style={{ transformOrigin: `${coreX}px ${coreY}px` }}
          />
          <motion.circle
            cx={coreX}
            cy={coreY}
            r={coreR}
            fill="hsl(var(--primary))"
            initial={{ scale: 0 }}
            animate={inView ? { scale: 1 } : {}}
            transition={{ delay: 0.4, type: "spring", stiffness: 180 }}
            style={{ transformOrigin: `${coreX}px ${coreY}px` }}
          />
          <motion.circle
            cx={coreX}
            cy={coreY}
            r={coreR - 8}
            fill="none"
            stroke="hsl(var(--primary-foreground))"
            strokeWidth={1.2}
            opacity={0.55}
            animate={{ rotate: 360 }}
            transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
            style={{ transformOrigin: `${coreX}px ${coreY}px` }}
            strokeDasharray="3 6"
          />
          <text
            x={coreX}
            y={coreY + 4}
            textAnchor="middle"
            className="fill-primary-foreground"
            style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1 }}
          >
            AI
          </text>
        </svg>
      </div>

      <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-2/3 h-20 bg-primary/30 blur-3xl rounded-full -z-10" />
    </motion.div>
  );
};

export default AiAnalysisPreview;
